// ===================================================
// Вход app.markbase.ru: единый аккаунт + email-код + OAuth-значки
// ===================================================
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { ShieldCheck, UserPlus, ArrowRight, Mail, User, KeyRound } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { message } from 'antd';
import axios from '../../utils/axios';
import { checkAuth } from '../../store/authSlice';
import AuthLayout from './AuthLayout';
import UnifiedAccountButton from '../../components/Auth/UnifiedAccountButton';
import ReviewerDemoAccessButton from '../../components/Auth/ReviewerDemoAccessButton';
import { PLATFORM_REVIEWER_EMAIL } from '../../config/platformReviewerAccount';
import { AUTH_MESSAGE_TYPE } from './AuthCallbackPage';
import {
  AUTH_BROADCAST_CHANNEL,
  AUTH_POPUP_LOGIN_NAME,
  AUTH_POPUP_REGISTER_NAME,
  closePopupRef,
} from './authPopup';
import {
  isSafeReturnUrl,
  buildRedirectTarget as buildRedirectTargetShared,
  resolvePostAuthTarget as resolvePostAuthTargetShared,
} from './postAuthRedirect';
import { useCaptcha } from '../../hooks/useCaptcha';
import { pickSafeRussianBackendMessage } from '../../utils/safeRussianBackendMessage';
import { getPublicDomainUrl } from '../../utils/constants';
import { saveLastLogin } from '../../utils/wsidAccounts';

const getUamUrl = () => {
  const raw = process.env.REACT_APP_UAM_URL || 'https://auth.markbase.ru';
  return raw.replace(/\/+$/, '');
};

const POPUP_WIDTH = 460;
const POPUP_HEIGHT = 700;

const OAUTH_BRAND = {
  yandex: { label: 'Яндекс', letter: 'Я', bg: '#FC3F1D' },
  vk: { label: 'VK', letter: 'VK', bg: '#0077FF' },
  mailru: { label: 'Mail.ru', letter: '@', bg: '#005FF9' },
  google: { label: 'Google', letter: 'G', bg: '#4285F4' },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[\d\s()-]{10,}$/;

function maskEmail(email) {
  if (!email || !email.includes('@')) return email;
  const [local, domain] = email.split('@');
  const masked = local.length <= 2 ? `${local[0]}***` : `${local.slice(0, 2)}***`;
  return `${masked}@${domain}`;
}

function buildCaptchaFields(captchaResult) {
  if (!captchaResult) return {};
  const fields = {};
  if (captchaResult.captcha_site_key) fields.captcha_site_key = captchaResult.captcha_site_key;
  if (captchaResult.captcha_provider) fields.captcha_provider = captchaResult.captcha_provider;
  if (captchaResult.challenge_id && captchaResult.captcha_answer) {
    fields.challenge_id = captchaResult.challenge_id;
    fields.captcha_answer = captchaResult.captcha_answer;
  } else if (captchaResult.captcha_token) {
    fields.captcha_token = captchaResult.captcha_token;
  }
  return fields;
}

function isCaptchaGateError(data) {
  return Boolean(
    data?.captcha_required
    || data?.captcha_failed
    || data?.code === 'CAPTCHA_REQUIRED'
    || data?.code === 'CAPTCHA_FAILED'
    || data?.code === 'CAPTCHA_UNAVAILABLE'
  );
}

export default function Login() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, checkingAuth, user, company } = useSelector((s) => s.auth);
  const hasNavigatedRef = useRef(false);
  const popupRef = useRef(null);
  const popupCheckIntervalRef = useRef(null);
  const authPollIntervalRef = useRef(null);

  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [oauthProviders, setOauthProviders] = useState([]);

  const captchaRequest = useCaptcha({ action: 'login_request_code' });
  const captchaVerify = useCaptcha({ action: 'login_verify_code' });
  const captchaLoading = captchaRequest.isExecuting || captchaVerify.isExecuting;

  const returnUrl = searchParams.get('return_url') || '';
  const switchEmail = searchParams.get('switch') || '';
  const reviewerDemo = searchParams.get('demo') === 'reviewer';
  const newAccount = searchParams.get('new_account') || '';
  const sessionExpired = searchParams.get('reason') === 'session_expired';
  const skipAutoRedirect =
    newAccount === '1' ||
    Boolean(switchEmail) ||
    searchParams.get('reauth') === '1' ||
    sessionExpired;
  const toPath = returnUrl && isSafeReturnUrl(returnUrl)
    ? returnUrl
    : '/dashboard';

  useEffect(() => {
    if (switchEmail) setEmail(String(switchEmail).trim());
  }, [switchEmail]);

  useEffect(() => {
    if (reviewerDemo) setEmail(PLATFORM_REVIEWER_EMAIL);
  }, [reviewerDemo]);

  useEffect(() => {
    let cancelled = false;
    const uam = getUamUrl();
    fetch(`${uam}/api/uam/v1/auth/providers`, { credentials: 'include' })
      .then((r) => r.json())
      .then((body) => {
        if (!cancelled) setOauthProviders(Array.isArray(body?.data) ? body.data : []);
      })
      .catch(() => {
        if (!cancelled) setOauthProviders([]);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const t = setInterval(() => setResendCooldown((v) => Math.max(0, v - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  const buildRedirectTarget = useCallback((path) => {
    return buildRedirectTargetShared(path || toPath);
  }, [toPath]);

  const resolvePostAuthTarget = useCallback((authState, requestedTarget, flow = null) => {
    const mergedState = {
      user: authState?.user || user,
      company: authState?.company || company || null,
    };
    return resolvePostAuthTargetShared(mergedState, requestedTarget || buildRedirectTarget(), flow);
  }, [buildRedirectTarget, user, company]);

  const doRedirect = useCallback((target) => {
    if (hasNavigatedRef.current) return;
    hasNavigatedRef.current = true;
    if (popupCheckIntervalRef.current) {
      clearInterval(popupCheckIntervalRef.current);
      popupCheckIntervalRef.current = null;
    }
    if (authPollIntervalRef.current) {
      clearInterval(authPollIntervalRef.current);
      authPollIntervalRef.current = null;
    }
    closePopupRef(popupRef);
    window.location.href = target || buildRedirectTarget();
  }, [buildRedirectTarget]);

  useEffect(() => {
    dispatch(checkAuth());
  }, [dispatch]);

  useEffect(() => {
    if (skipAutoRedirect) return;
    if (!checkingAuth && (isAuthenticated || user) && !hasNavigatedRef.current) {
      doRedirect(resolvePostAuthTarget({ user, company }, buildRedirectTarget()));
    }
  }, [checkingAuth, isAuthenticated, user, company, doRedirect, buildRedirectTarget, resolvePostAuthTarget, skipAutoRedirect]);

  useEffect(() => {
    const onAuthSuccess = async (data) => {
      if (hasNavigatedRef.current) return;
      if (data?.type !== AUTH_MESSAGE_TYPE) return;
      try {
        const authState = await dispatch(checkAuth()).unwrap();
        if (!authState?.isAuthenticated) return;
        const target = resolvePostAuthTarget(
          authState,
          buildRedirectTarget(data.to || '/dashboard'),
          data.flow || null,
        );
        doRedirect(target);
      } catch (error) {
        console.warn('Проверка авторизации после callback не удалась:', error);
      }
    };

    const handler = (event) => {
      if (event.origin !== window.location.origin) return;
      onAuthSuccess(event.data);
    };

    window.addEventListener('message', handler);
    let channel = null;
    try {
      channel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
      channel.onmessage = (event) => onAuthSuccess(event.data);
    } catch (_) { /* ignore */ }
    return () => {
      window.removeEventListener('message', handler);
      try { channel?.close(); } catch (_) { /* ignore */ }
    };
  }, [dispatch, buildRedirectTarget, doRedirect, resolvePostAuthTarget]);

  const checkAuthAndRedirect = useCallback(async (flow = null, requestedTarget = null) => {
    if (hasNavigatedRef.current) return;
    const delays = [300, 800, 1500, 2500, 4000];
    for (let attempt = 0; attempt < delays.length; attempt++) {
      if (hasNavigatedRef.current) return;
      try {
        const result = await dispatch(checkAuth()).unwrap();
        if (result && result.isAuthenticated) {
          doRedirect(resolvePostAuthTarget(result, requestedTarget || buildRedirectTarget(), flow));
          return;
        }
      } catch (error) {
        console.warn(`Auth check attempt ${attempt + 1}/${delays.length} failed:`, error);
      }
      if (attempt < delays.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, delays[attempt]));
      }
    }
  }, [dispatch, doRedirect, buildRedirectTarget, resolvePostAuthTarget]);

  useEffect(() => () => {
    if (authPollIntervalRef.current) {
      clearInterval(authPollIntervalRef.current);
      authPollIntervalRef.current = null;
    }
  }, []);

  const startPopupAuthPoll = useCallback((flow = null) => {
    if (authPollIntervalRef.current) clearInterval(authPollIntervalRef.current);
    const authPollTimer = setTimeout(() => {
      if (hasNavigatedRef.current) return;
      authPollIntervalRef.current = setInterval(async () => {
        if (hasNavigatedRef.current) {
          clearInterval(authPollIntervalRef.current);
          authPollIntervalRef.current = null;
          return;
        }
        try {
          const result = await dispatch(checkAuth()).unwrap();
          if (result && result.isAuthenticated) {
            doRedirect(resolvePostAuthTarget(result, buildRedirectTarget(), flow));
          }
        } catch (_) { /* next tick */ }
      }, 5000);
    }, 5000);

    const checkPopupClosed = setInterval(() => {
      if (popupRef.current && popupRef.current.closed) {
        clearInterval(checkPopupClosed);
        popupCheckIntervalRef.current = null;
        popupRef.current = null;
        checkAuthAndRedirect(flow);
      }
    }, 500);
    popupCheckIntervalRef.current = checkPopupClosed;

    setTimeout(() => {
      if (popupCheckIntervalRef.current === checkPopupClosed) {
        clearInterval(checkPopupClosed);
        popupCheckIntervalRef.current = null;
      }
      if (authPollIntervalRef.current) {
        clearInterval(authPollIntervalRef.current);
        authPollIntervalRef.current = null;
      }
      clearTimeout(authPollTimer);
    }, 5 * 60 * 1000);
  }, [checkAuthAndRedirect, dispatch, doRedirect, buildRedirectTarget, resolvePostAuthTarget]);

  const openLoginPopup = useCallback((extraParams = {}) => {
    const uam = getUamUrl();
    const callbackTo = `${window.location.origin}/auth/callback?to=${encodeURIComponent(toPath)}`;
    const params = new URLSearchParams({ return_url: callbackTo, prompt: 'login', ...extraParams });
    if (switchEmail) params.set('switch', switchEmail);
    if (newAccount) params.set('new_account', newAccount);
    const url = `${uam}/login?${params.toString()}`;
    const left = Math.round((window.screen.width - POPUP_WIDTH) / 2);
    const top = Math.round((window.screen.height - POPUP_HEIGHT) / 2);
    const opts = `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},scrollbars=yes,resizable=yes`;
    popupRef.current = window.open(url, AUTH_POPUP_LOGIN_NAME, opts);
    if (popupRef.current) startPopupAuthPoll(null);
  }, [toPath, switchEmail, newAccount, startPopupAuthPoll]);

  const openReviewerDemo = useCallback(() => {
    setEmail(PLATFORM_REVIEWER_EMAIL);
    openLoginPopup({ prompt: 'login', login_hint: PLATFORM_REVIEWER_EMAIL });
  }, [openLoginPopup]);

  const openRegisterPopup = useCallback(() => {
    const uam = getUamUrl();
    const callbackTo = `${window.location.origin}/auth/callback?to=${encodeURIComponent(toPath)}`;
    const regParams = new URLSearchParams({ return_url: callbackTo, new_account: '1' });
    const url = `${uam}/register?${regParams.toString()}`;
    const left = Math.round((window.screen.width - POPUP_WIDTH) / 2);
    const top = Math.round((window.screen.height - POPUP_HEIGHT) / 2);
    const opts = `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},scrollbars=yes,resizable=yes`;
    popupRef.current = window.open(url, AUTH_POPUP_REGISTER_NAME, opts);
    if (popupRef.current) startPopupAuthPoll('register');
  }, [toPath, startPopupAuthPoll]);

  const runCaptcha = useCallback(async (kind) => {
    const exec = kind === 'verify' ? captchaVerify.executeCaptcha : captchaRequest.executeCaptcha;
    const reset = kind === 'verify' ? captchaVerify.resetCaptcha : captchaRequest.resetCaptcha;
    // Не создаём bypass-token: outage обрабатывается UAM как CAPTCHA_UNAVAILABLE.
    // Пустые поля → UAM сам решит через need-captcha (trusted skip) или вернёт captcha_required.
    const result = await exec();
    return {
      fields: buildCaptchaFields(result),
      reset,
      hasCaptcha: Boolean(result && !result.captcha_unavailable),
      unavailable: result?.captcha_unavailable === true,
    };
  }, [captchaRequest.executeCaptcha, captchaRequest.resetCaptcha, captchaVerify.executeCaptcha, captchaVerify.resetCaptcha]);

  const requestEmailCode = useCallback(async () => {
    const trimmed = email.trim().replace(/\s/g, '');
    if (!trimmed) {
      setFormError('Введите email');
      return;
    }
    if (PHONE_RE.test(trimmed) && !trimmed.includes('@')) {
      setFormError('');
      openLoginPopup({ login_hint: trimmed });
      return;
    }
    if (!EMAIL_RE.test(trimmed)) {
      setFormError('Введите корректный email');
      return;
    }

    setBusy(true);
    setFormError('');
    let captchaPack = await runCaptcha('request');
    if (captchaPack.unavailable) {
      setFormError('Проверка безопасности временно недоступна. Данные сохранены — попробуйте позже.');
      setBusy(false);
      return;
    }
    try {
      const post = (fields) => axios.post(
        `${getUamUrl()}/api/uam/v1/login/request-code`,
        { email: trimmed, ...fields },
        { withCredentials: true },
      );
      try {
        await post(captchaPack.fields);
      } catch (firstErr) {
        const data = firstErr.response?.data || {};
        if (isCaptchaGateError(data)) {
          captchaPack.reset();
          captchaPack = await runCaptcha('request');
          if (captchaPack.unavailable) throw firstErr;
          if (!Object.keys(captchaPack.fields).length) {
            throw firstErr;
          }
          await post(captchaPack.fields);
        } else {
          throw firstErr;
        }
      }
      setEmail(trimmed);
      setOtpSent(true);
      setOtpCode('');
      setResendCooldown(60);
      message.success(`Код отправлен на ${maskEmail(trimmed)}`);
      captchaPack.reset();
    } catch (err) {
      const data = err.response?.data || {};
      setFormError(pickSafeRussianBackendMessage(
        data.error,
        isCaptchaGateError(data)
          ? 'Пройдите проверку безопасности и запросите код снова'
          : 'Не удалось отправить код',
      ));
      captchaPack.reset();
    } finally {
      setBusy(false);
    }
  }, [email, openLoginPopup, runCaptcha]);

  const verifyEmailCode = useCallback(async () => {
    const code = String(otpCode || '').replace(/\D/g, '').slice(0, 6);
    if (code.length !== 6) {
      setFormError('Введите 6-значный код из письма');
      return;
    }
    setBusy(true);
    setFormError('');
    let captchaPack = await runCaptcha('verify');
    if (captchaPack.unavailable) {
      setFormError('Проверка безопасности временно недоступна. Код и email сохранены — попробуйте позже.');
      setBusy(false);
      return;
    }
    try {
      const callbackTo = `${window.location.origin}/auth/callback?to=${encodeURIComponent(toPath)}`;
      const post = (fields) => axios.post(
        `${getUamUrl()}/api/uam/v1/login/verify-code`,
        {
          email: email.trim(),
          code,
          return_url: callbackTo,
          ...fields,
        },
        { withCredentials: true },
      );
      let res;
      try {
        res = await post(captchaPack.fields);
      } catch (firstErr) {
        const data = firstErr.response?.data || {};
        if (isCaptchaGateError(data)) {
          captchaPack.reset();
          captchaPack = await runCaptcha('verify');
          if (captchaPack.unavailable) throw firstErr;
          if (!Object.keys(captchaPack.fields).length) {
            throw firstErr;
          }
          res = await post(captchaPack.fields);
        } else {
          throw firstErr;
        }
      }
      const data = res.data || {};
      if (data.requires_totp || data.totp_required) {
        openLoginPopup({ switch: email.trim() });
        return;
      }
      if (data.return_url_not_allowed) {
        setFormError('Сервис возврата не разрешён в UAM. Обратитесь к администратору.');
        return;
      }
      if (data.needs_consents === true) {
        const continueTarget = data.return_url || callbackTo;
        window.location.href = `${getUamUrl()}/account/consents?continue=${encodeURIComponent(continueTarget)}`;
        return;
      }
      if (data.success && data.user?.email) {
        try {
          saveLastLogin(data.user.email, data.user.display_name, {
            quick_login: data.quick_login === true,
            require_password_on_switch: data.require_password_on_switch,
            switch_inactivity_days: data.switch_inactivity_days,
          });
        } catch (_) { /* ignore */ }
      }
      const authState = await dispatch(checkAuth()).unwrap();
      if (authState?.isAuthenticated) {
        doRedirect(resolvePostAuthTarget(authState, buildRedirectTarget(), null));
        return;
      }
      await checkAuthAndRedirect(null);
    } catch (err) {
      const data = err.response?.data || {};
      setFormError(pickSafeRussianBackendMessage(
        data.error,
        isCaptchaGateError(data)
          ? 'Пройдите проверку безопасности и подтвердите код снова'
          : 'Неверный или истёкший код',
      ));
      captchaPack.reset();
    } finally {
      setBusy(false);
    }
  }, [
    otpCode, email, toPath, runCaptcha, openLoginPopup,
    dispatch, doRedirect, resolvePostAuthTarget, buildRedirectTarget, checkAuthAndRedirect,
  ]);

  const handlePrimarySubmit = useCallback((e) => {
    e.preventDefault();
    if (otpSent) verifyEmailCode();
    else requestEmailCode();
  }, [otpSent, verifyEmailCode, requestEmailCode]);

  const handleOAuth = useCallback((provider) => {
    const callbackTo = `${window.location.origin}/auth/callback?to=${encodeURIComponent(toPath)}`;
    const url = `${getUamUrl()}/api/uam/v1/oauth/${provider}?return_url=${encodeURIComponent(callbackTo)}`;
    window.location.href = url;
  }, [toPath]);

  const publicUrl = (path) => getPublicDomainUrl(path);
  const submitting = busy || captchaLoading;

  return (
    <AuthLayout>
      <div className="mb-login">
        {sessionExpired && (
          <div className="mb-login__notice" role="alert">
            Сессия истекла или вход не выполнен. Войдите снова, чтобы продолжить работу.
          </div>
        )}
        {!checkingAuth && isAuthenticated && user && skipAutoRedirect && !sessionExpired && (
          <div className="mb-login__notice">
            Вы уже вошли как <strong>{user.email || 'пользователь'}</strong>.
            Кнопки ниже откроют единый аккаунт Маркбэйс id для смены аккаунта.
          </div>
        )}

        <div className="mb-login__header">
          <h3 className="mb-login__title">Вход в Маркбэйс</h3>
          <span className="mb-login__subtitle">
            Единый доступ ко всем модулям платформы
          </span>
        </div>

        <UnifiedAccountButton
          type="button"
          variant="outline"
          size="lg"
          fullWidth
          className="mb-login__cta mb-login__cta--unified"
          onClick={() => openLoginPopup()}
        />

        <ReviewerDemoAccessButton
          onClick={openReviewerDemo}
          disabled={submitting}
        />

        <div className="mb-login__divider">
          <span>или по email</span>
        </div>

        <form className="mb-login__form" onSubmit={handlePrimarySubmit} noValidate>
          {!otpSent ? (
            <label className="mb-login__field">
              <span className="mb-login__field-label">Email</span>
              <span className="mb-login__field-control">
                <User size={16} aria-hidden className="mb-login__field-icon" />
                <input
                  type="email"
                  name="email"
                  autoComplete="username"
                  inputMode="email"
                  placeholder="Email или телефон"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value.replace(/\s/g, ''));
                    setFormError('');
                  }}
                  disabled={submitting}
                />
              </span>
            </label>
          ) : (
            <>
              <div className="mb-login__otp-meta">
                <Mail size={14} aria-hidden />
                <span>Код отправлен на {maskEmail(email)}</span>
                <button
                  type="button"
                  className="mb-login__link-btn"
                  onClick={() => {
                    setOtpSent(false);
                    setOtpCode('');
                    setFormError('');
                  }}
                  disabled={submitting}
                >
                  Изменить
                </button>
              </div>
              <label className="mb-login__field">
                <span className="mb-login__field-label">Код из письма</span>
                <span className="mb-login__field-control">
                  <KeyRound size={16} aria-hidden className="mb-login__field-icon" />
                  <input
                    type="text"
                    name="otp"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="6 цифр"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) => {
                      setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                      setFormError('');
                    }}
                    disabled={submitting}
                    autoFocus
                  />
                </span>
              </label>
              <div className="mb-login__resend">
                <button
                  type="button"
                  className="mb-login__link-btn"
                  disabled={submitting || resendCooldown > 0}
                  onClick={requestEmailCode}
                >
                  {resendCooldown > 0 ? `Отправить снова через ${resendCooldown} с` : 'Отправить код снова'}
                </button>
              </div>
            </>
          )}

          {formError && (
            <div className="mb-login__error" role="alert">{formError}</div>
          )}

          <button
            type="submit"
            className="mb-login__submit"
            disabled={submitting}
          >
            <span>{otpSent ? 'Подтвердить и войти' : 'Войти'}</span>
            <ArrowRight size={18} aria-hidden />
          </button>
        </form>

        {oauthProviders.length > 0 && (
          <div className="mb-login__oauth">
            <div className="mb-login__divider">
              <span>быстрый вход</span>
            </div>
            <div className="mb-login__oauth-row" role="group" aria-label="Социальные сети">
              {oauthProviders.map((p) => {
                const brand = OAUTH_BRAND[p.provider] || {
                  label: p.display_name || p.provider,
                  letter: String(p.provider || '?').slice(0, 1).toUpperCase(),
                  bg: '#334155',
                };
                return (
                  <button
                    key={p.provider}
                    type="button"
                    className="mb-login__oauth-btn"
                    title={brand.label}
                    aria-label={`Войти через ${brand.label}`}
                    onClick={() => handleOAuth(p.provider)}
                    disabled={submitting}
                  >
                    <span className="mb-login__oauth-badge" style={{ backgroundColor: brand.bg }}>
                      {brand.letter}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="mb-login__divider">
          <span>или</span>
        </div>

        <button
          type="button"
          onClick={openRegisterPopup}
          className="mb-login__register-btn"
          disabled={submitting}
        >
          <UserPlus size={16} aria-hidden />
          Зарегистрироваться бесплатно
        </button>

        <p className="mb-login__legal">
          Продолжая, вы принимаете{' '}
          <a href={publicUrl('/terms')}>условия использования</a>
          {' '}и{' '}
          <a href={publicUrl('/privacy')}>политику конфиденциальности</a>.
        </p>

        <div className="mb-login__howto">
          <div className="mb-login__howto-title">Как начать работу</div>
          <ol className="mb-login__howto-steps">
            <li>
              <span className="mb-login__howto-num">1</span>
              <span>Войдите через единый аккаунт или код на email</span>
            </li>
            <li>
              <span className="mb-login__howto-num">2</span>
              <span>Выберите нужный модуль в панели</span>
            </li>
            <li>
              <span className="mb-login__howto-num">3</span>
              <span>Продолжайте работу в одном контуре</span>
            </li>
          </ol>
        </div>

        <div className="mb-login__hint">
          <ShieldCheck size={14} aria-hidden />
          <span>Единый аккаунт — удобный вход в экосистему. Email-код и соцсети защищены капчей и лимитами.</span>
        </div>
      </div>
    </AuthLayout>
  );
}
